import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/** Settings → Customer events → Meta Conversions API. The access token is never sent back: only its last 4. */
export type CapiStatus = {
  pixel_id: string | null
  connected: boolean
  readable: boolean
  token_last4: string | null
  test_event_code: string | null
  test_code_on_live: boolean
  enabled: boolean
  send_renewals: boolean
  /** Which id the pixel and the Conversions API send to Meta as content_ids / contents. */
  content_id: CapiContentId
  active: boolean
  last_test_at: string | null
  last_test_ok: boolean | null
  connected_at: string | null
  last_24h: { pending: number; sent: number; failed: number; skipped: number }
  last_event: { event_name: string; status: string; last_error: string | null; sent_at: string | null; updated_at: string } | null
  last_failure: { event_name: string; last_error: string | null; updated_at: string } | null
  /** Events kept back because Meta refused the access token; sent as soon as a working token is saved. */
  paused_waiting: number
  can_manage: boolean
}
export type CapiTestResult = { ok: boolean; status: number; message: string | null; response: unknown; payload: unknown; test_event_code?: string | null }
type StatusResponse = { capi: CapiStatus }

const KEY = ["settings", "conversions-api"]
const PATH = "/admin/terify/settings/conversions-api"

export function useConversionsApi() {
  return useQuery({ queryKey: KEY, queryFn: () => sdk.client.fetch<StatusResponse>(PATH) })
}

function useCapiMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r: any) => {
      if (r?.capi) qc.setQueryData(KEY, r)
      else qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export type CapiContentId = "sku" | "product_id" | "variant_id"
export const CONTENT_ID_LABEL: Record<CapiContentId, string> = { sku: "SKU", product_id: "Product ID", variant_id: "Variant ID" }
export type CapiSave = { access_token?: string; test_event_code?: string | null; test_code_on_live?: boolean; enabled?: boolean; send_renewals?: boolean; content_id?: CapiContentId }
export const useSaveConversionsApi = () => useCapiMutation((body: CapiSave) => sdk.client.fetch<StatusResponse>(PATH, { method: "POST", body }))
export const useRemoveConversionsApiToken = () => useCapiMutation(() => sdk.client.fetch<StatusResponse>(PATH, { method: "DELETE" }))
export const useSendConversionsApiTest = () => useCapiMutation((body: { test_event_code?: string | null }) => sdk.client.fetch<{ test: CapiTestResult }>(`${PATH}/send-test`, { method: "POST", body }))
